// src/pages/settings/SettingsPage.tsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Switch,
  Button,
  Snackbar,
  Alert,
  Grid,
  Card,
  CardContent,
  CardHeader,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Tabs,
  Tab,
  Divider,
  alpha,
  useMediaQuery,
  Fade,
  Zoom,
  Chip,
} from "@mui/material";
import {
  Logout as LogoutIcon,
  Lock as LockIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Notifications as NotificationsIcon,
  VolumeUp as VolumeUpIcon,
  Security as SecurityIcon,
  Palette as PaletteIcon,
  Language as LanguageIcon,
  Storage as StorageIcon,
  Speed as SpeedIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Settings as SettingsIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTheme, styled, keyframes } from "@mui/material/styles";

// Advanced Styled Components
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;


const GlassCard = styled(Card)(({ theme }) => ({
  background:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.background.paper, 0.85)
      : alpha("#ffffff", 0.95),
  backdropFilter: "saturate(180%) blur(20px)",
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  borderRadius: 20,
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 8px 32px rgba(0, 0, 0, 0.4)"
      : "0 8px 32px rgba(0, 0, 0, 0.08)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  animation: `${fadeInUp} 0.6s ease-out`,
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 16px 48px rgba(0, 0, 0, 0.5)"
        : "0 16px 48px rgba(0, 0, 0, 0.12)",
  },
  overflow: "visible",
}));

const SettingCard = styled(GlassCard)(({ theme }) => ({
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.8)}, ${alpha(theme.palette.secondary?.main || theme.palette.primary.main, 0.8)})`,
    borderRadius: "20px 20px 0 0",
  },
}));

const IconContainer = styled(Box)(({ theme }) => ({
  width: 56,
  height: 56,
  borderRadius: 16,
  display: "grid",
  placeItems: "center",
  position: "relative",
  transition: "all 0.3s ease",
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.secondary?.main || theme.palette.primary.main, 0.15)})`,
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: 16,
    padding: 2,
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.3)}, ${alpha(theme.palette.secondary?.main || theme.palette.primary.main, 0.3)})`,
    WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
    WebkitMaskComposite: "xor",
    maskComposite: "exclude",
    opacity: 0,
    transition: "opacity 0.3s ease",
  },
  "&:hover::after": {
    opacity: 1,
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 700,
  fontSize: "0.938rem",
  minHeight: 48,
  borderRadius: 12,
  marginRight: theme.spacing(1),
  transition: "all 0.3s ease",
  "&.Mui-selected": {
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.secondary?.main || theme.palette.primary.main, 0.15)})`,
    color: theme.palette.primary.main,
  },
  "&:hover": {
    background: alpha(theme.palette.primary.main, 0.08),
  },
}));

const SettingItem = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 12,
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  transition: "all 0.2s ease",
  "&:hover": {
    background: alpha(theme.palette.primary.main, 0.04),
    borderColor: alpha(theme.palette.primary.main, 0.2),
  },
}));

interface SettingsPageProps {
  toggleTheme: () => void; // switches app theme
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Fade in={value === index} timeout={600}>
          <Box sx={{ p: { xs: 2, md: 3 } }}>{children}</Box>
        </Fade>
      )}
    </div>
  );
}

const SettingsPage: React.FC<SettingsPageProps> = ({ toggleTheme }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // --------------------
  // State management
  // --------------------
  const [tabValue, setTabValue] = useState(0);
  const [darkMode, setDarkMode] = useState(false);
  const [notifEmail, setNotifEmail] = useState<boolean>(true);
  const [notifSound, setNotifSound] = useState<boolean>(false);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);
  const [compactMode, setCompactMode] = useState<boolean>(false);
  const [language, setLanguage] = useState<string>("en");
  const [fontSize, setFontSize] = useState<number>(14);
  const [busy, setBusy] = useState<boolean>(false);

  // feedback + dialogs
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "info" | "warning" | "error";
  }>({ open: false, message: "", severity: "success" });

  const [confirmLogoutOpen, setConfirmLogoutOpen] = useState(false);

  // --------------------
  // Load persisted preferences
  // --------------------
  useEffect(() => {
    // theme
    const savedMode = localStorage.getItem("themeMode");
    setDarkMode(savedMode === "dark");

    // notification prefs
    setNotifEmail(localStorage.getItem("pref_email_notif") !== "0");
    setNotifSound(localStorage.getItem("pref_sound_notif") === "1");
    setAutoRefresh(localStorage.getItem("pref_auto_refresh") !== "0");
    setCompactMode(localStorage.getItem("pref_compact_mode") === "1");
    
    // language and font size
    setLanguage(localStorage.getItem("pref_language") || "en");
    setFontSize(Number(localStorage.getItem("pref_font_size")) || 14);
  }, []);

  // --------------------
  // Keyboard shortcuts
  // Ctrl/Cmd+J toggles theme
  // --------------------
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().includes("MAC");
      const key = (e.key || "").toLowerCase();
      const toggleThemePressed =
        (isMac && e.metaKey && key === "j") || (!isMac && e.ctrlKey && key === "j");

      if (toggleThemePressed) {
        e.preventDefault();
        handleThemeToggle();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [darkMode]);

  // --------------------
  // Handlers
  // --------------------
  const handleThemeToggle = () => {
    const next = !darkMode;
    setDarkMode(next);
    // Let App flip the palette
    toggleTheme();
    // Persist
    localStorage.setItem("themeMode", next ? "dark" : "light");
    setSnackbar({
      open: true,
      message: `Switched to ${next ? "Dark" : "Light"} Mode`,
      severity: "success",
    });
  };


  const handleNavigate = (path: string) => {
    if (path === "log out") {
      setConfirmLogoutOpen(true);
    } else {
      navigate(path);
    }
  };

  const doLogout = async () => {
    try {
      setBusy(true);
      // if you have a server logout endpoint, call it here
      localStorage.removeItem("accessToken");
      navigate("/");
    } finally {
      setBusy(false);
      setConfirmLogoutOpen(false);
    }
  };

  const handleNotifEmail = (checked: boolean) => {
    setNotifEmail(checked);
    localStorage.setItem("pref_email_notif", checked ? "1" : "0");
  };

  const handleNotifSound = (checked: boolean) => {
    setNotifSound(checked);
    localStorage.setItem("pref_sound_notif", checked ? "1" : "0");
  };

  const handleAutoRefresh = (checked: boolean) => {
    setAutoRefresh(checked);
    localStorage.setItem("pref_auto_refresh", checked ? "1" : "0");
  };

  const handleCompactMode = (checked: boolean) => {
    setCompactMode(checked);
    localStorage.setItem("pref_compact_mode", checked ? "1" : "0");
  };

  const handleLanguageChange = (newLanguage: string) => {
    setLanguage(newLanguage);
    localStorage.setItem("pref_language", newLanguage);
    setSnackbar({
      open: true,
      message: `Language changed to ${newLanguage === "en" ? "English" : "العربية"}`,
      severity: "success",
    });
  };

  const handleFontSizeChange = (newSize: number) => {
    setFontSize(newSize);
    localStorage.setItem("pref_font_size", newSize.toString());
    // Apply font size to document
    document.documentElement.style.fontSize = `${newSize}px`;
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // --------------------
  // UI
  // --------------------
  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        minHeight: "100vh",
        background:
          theme.palette.mode === "dark"
            ? "radial-gradient(1200px 600px at 10% -10%, rgba(93,176,255,0.08), transparent), linear-gradient(180deg, rgba(255,255,255,0.02), transparent)"
            : "radial-gradient(1200px 600px at 10% -10%, rgba(93,176,255,0.10), transparent), linear-gradient(180deg, #F7FAFF, #ECF3FF)",
      }}
    >
      {/* Header */}
      <Fade in timeout={600}>
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" spacing={2} mb={2}>
            <Zoom in timeout={800}>
              <IconContainer>
                <SettingsIcon color="primary" fontSize="large" />
              </IconContainer>
            </Zoom>
            <Box>
              <Typography variant={isMobile ? "h4" : "h3"} fontWeight={800} gutterBottom>
                Settings
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Manage your account settings, preferences, and security options
              </Typography>
            </Box>
          </Stack>
          {busy && (
            <LinearProgress
              sx={{
                mb: 2,
                height: 4,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
              }}
            />
          )}
        </Box>
      </Fade>

      {/* Tabs for different settings sections */}
      <GlassCard sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            px: { xs: 1, md: 3 },
            pt: 2,
            "& .MuiTabs-indicator": {
              height: 3,
              borderRadius: "3px 3px 0 0",
            },
          }}
        >
          <StyledTab icon={<PaletteIcon />} iconPosition="start" label="Appearance" />
          <StyledTab icon={<NotificationsIcon />} iconPosition="start" label="Notifications" />
          <StyledTab icon={<SecurityIcon />} iconPosition="start" label="Security" />
          <StyledTab icon={<LanguageIcon />} iconPosition="start" label="Language" />
          <StyledTab icon={<SpeedIcon />} iconPosition="start" label="Performance" />
        </Tabs>

        {/* Appearance Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <SettingCard>
                <CardHeader
                  title={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <PaletteIcon color="primary" />
                      <Typography variant="h6" fontWeight={700}>
                        Theme Settings
                      </Typography>
                    </Stack>
                  }
                />
                <Divider />
                <CardContent>
                  <Stack spacing={3}>
                    <SettingItem>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          {darkMode ? <DarkModeIcon color="primary" /> : <LightModeIcon color="primary" />}
                          <Box>
                            <Typography variant="subtitle1" fontWeight={600}>
                              Color Theme
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Shortcut: Ctrl/Cmd + J
                            </Typography>
                          </Box>
                        </Stack>
                        <Switch
                          checked={darkMode}
                          onChange={handleThemeToggle}
                          color="primary"
                        />
                      </Stack>
                      {darkMode && (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Dark Mode Active"
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ mt: 1 }}
                        />
                      )}
                    </SettingItem>

                    <SettingItem>
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        Font Size: {fontSize}px
                      </Typography>
                      <Slider
                        value={fontSize}
                        onChange={(_, newValue) => handleFontSizeChange(newValue as number)}
                        min={12}
                        max={20}
                        step={1}
                        marks
                        valueLabelDisplay="auto"
                        color="primary"
                        sx={{ mt: 2 }}
                      />
                      <Stack direction="row" justifyContent="space-between" mt={1}>
                        <Typography variant="caption" color="text.secondary">
                          Small
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Large
                        </Typography>
                      </Stack>
                    </SettingItem>

                    <SettingItem>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>
                            Compact Mode
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Reduce spacing for more content
                          </Typography>
                        </Box>
                        <Switch
                          checked={compactMode}
                          onChange={(e) => handleCompactMode(e.target.checked)}
                          color="primary"
                        />
                      </Stack>
                    </SettingItem>
                  </Stack>
                </CardContent>
              </SettingCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <SettingCard>
                <CardHeader
                  title={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <SettingsIcon color="primary" />
                      <Typography variant="h6" fontWeight={700}>
                        Interface Settings
                      </Typography>
                    </Stack>
                  }
                />
                <Divider />
                <CardContent>
                  <Stack spacing={3}>
                    <SettingItem>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>
                            Auto Refresh
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Automatically refresh data every 30 seconds
                          </Typography>
                        </Box>
                        <Switch
                          checked={autoRefresh}
                          onChange={(e) => handleAutoRefresh(e.target.checked)}
                          color="primary"
                        />
                      </Stack>
                    </SettingItem>

                    <SettingItem>
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        Display Density
                      </Typography>
                      <FormControl fullWidth sx={{ mt: 1 }}>
                        <InputLabel>Density</InputLabel>
                        <Select
                          value={compactMode ? "compact" : "comfortable"}
                          onChange={(e) => handleCompactMode(e.target.value === "compact")}
                          label="Density"
                        >
                          <MenuItem value="comfortable">
                            <Stack direction="row" spacing={1} alignItems="center">
                              <InfoIcon fontSize="small" />
                              <span>Comfortable</span>
                            </Stack>
                          </MenuItem>
                          <MenuItem value="compact">
                            <Stack direction="row" spacing={1} alignItems="center">
                              <InfoIcon fontSize="small" />
                              <span>Compact</span>
                            </Stack>
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </SettingItem>
                  </Stack>
                </CardContent>
              </SettingCard>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Notifications Tab */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <SettingCard>
                <CardHeader
                  title={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <NotificationsIcon color="primary" />
                      <Typography variant="h6" fontWeight={700}>
                        Notification Preferences
                      </Typography>
                    </Stack>
                  }
                />
                <Divider />
                <CardContent>
                  <Stack spacing={3}>
                    <SettingItem>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" spacing={2} alignItems="center" flex={1}>
                          <IconContainer sx={{ width: 48, height: 48 }}>
                            <EmailIcon color="primary" />
                          </IconContainer>
                          <Box>
                            <Typography variant="subtitle1" fontWeight={600}>
                              Email Notifications
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Receive email alerts for important updates
                            </Typography>
                          </Box>
                        </Stack>
                        <Switch
                          checked={notifEmail}
                          onChange={(e) => handleNotifEmail(e.target.checked)}
                          color="primary"
                        />
                      </Stack>
                      {notifEmail && (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Enabled"
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ mt: 1 }}
                        />
                      )}
                    </SettingItem>

                    <Divider />

                    <SettingItem>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" spacing={2} alignItems="center" flex={1}>
                          <IconContainer sx={{ width: 48, height: 48 }}>
                            <VolumeUpIcon color="primary" />
                          </IconContainer>
                          <Box>
                            <Typography variant="subtitle1" fontWeight={600}>
                              Sound Notifications
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Play sound when new notifications arrive
                            </Typography>
                          </Box>
                        </Stack>
                        <Switch
                          checked={notifSound}
                          onChange={(e) => handleNotifSound(e.target.checked)}
                          color="primary"
                        />
                      </Stack>
                      {notifSound && (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Enabled"
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ mt: 1 }}
                        />
                      )}
                    </SettingItem>
                  </Stack>
                </CardContent>
              </SettingCard>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Security Tab */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <SettingCard>
                <CardHeader
                  title={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <SecurityIcon color="primary" />
                      <Typography variant="h6" fontWeight={700}>
                        Security Settings
                      </Typography>
                    </Stack>
                  }
                />
                <Divider />
                <CardContent>
                  <Stack spacing={2}>
                    <Button
                      variant="outlined"
                      startIcon={<LockIcon />}
                      onClick={() => handleNavigate("/change-password")}
                      fullWidth
                      sx={{ py: 1.5, borderRadius: 2 }}
                      size="large"
                    >
                      Change Password
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      fullWidth
                      sx={{ py: 1.5, borderRadius: 2 }}
                      size="large"
                    >
                      Two-Factor Authentication
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<AssessmentIcon />}
                      fullWidth
                      sx={{ py: 1.5, borderRadius: 2 }}
                      size="large"
                    >
                      Login History
                    </Button>
                  </Stack>
                </CardContent>
              </SettingCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <SettingCard>
                <CardHeader
                  title={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CalendarIcon color="primary" />
                      <Typography variant="h6" fontWeight={700}>
                        Session Management
                      </Typography>
                    </Stack>
                  }
                />
                <Divider />
                <CardContent>
                  <Stack spacing={2}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        background: alpha(theme.palette.success.main, 0.1),
                        border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CheckCircleIcon color="success" fontSize="small" />
                        <Typography variant="body2" color="success.main" fontWeight={600}>
                          Current session is active
                        </Typography>
                      </Stack>
                    </Box>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<LogoutIcon />}
                      onClick={() => setConfirmLogoutOpen(true)}
                      fullWidth
                      sx={{ py: 1.5, borderRadius: 2 }}
                      size="large"
                    >
                      Sign Out All Devices
                    </Button>
                  </Stack>
                </CardContent>
              </SettingCard>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Language Tab */}
        <TabPanel value={tabValue} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <SettingCard>
                <CardHeader
                  title={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <LanguageIcon color="primary" />
                      <Typography variant="h6" fontWeight={700}>
                        Language & Region
                      </Typography>
                    </Stack>
                  }
                />
                <Divider />
                <CardContent>
                  <Stack spacing={3}>
                    <SettingItem>
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        Language
                      </Typography>
                      <FormControl fullWidth sx={{ mt: 1 }}>
                        <InputLabel>Select Language</InputLabel>
                        <Select
                          value={language}
                          onChange={(e) => handleLanguageChange(e.target.value)}
                          label="Select Language"
                        >
                          <MenuItem value="en">
                            <Stack direction="row" spacing={1} alignItems="center">
                              <LanguageIcon fontSize="small" />
                              <span>English</span>
                            </Stack>
                          </MenuItem>
                          <MenuItem value="ar">
                            <Stack direction="row" spacing={1} alignItems="center">
                              <LanguageIcon fontSize="small" />
                              <span>العربية</span>
                            </Stack>
                          </MenuItem>
                        </Select>
                      </FormControl>
                      {language && (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label={`Current: ${language === "en" ? "English" : "العربية"}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ mt: 2 }}
                        />
                      )}
                    </SettingItem>

                    <Divider />

                    <SettingItem>
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        Time Zone
                      </Typography>
                      <FormControl fullWidth sx={{ mt: 1 }}>
                        <InputLabel>Time Zone</InputLabel>
                        <Select value="UTC+3" disabled label="Time Zone">
                          <MenuItem value="UTC+3">UTC+3 (GMT+3)</MenuItem>
                        </Select>
                      </FormControl>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                        Time zone settings are managed by your workspace administrator
                      </Typography>
                    </SettingItem>

                    <Divider />

                    <SettingItem>
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        Date Format
                      </Typography>
                      <FormControl fullWidth sx={{ mt: 1 }}>
                        <InputLabel>Date Format</InputLabel>
                        <Select value="MM/DD/YYYY" disabled label="Date Format">
                          <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
                          <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
                        </Select>
                      </FormControl>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                        Date format settings are managed by your workspace administrator
                      </Typography>
                    </SettingItem>
                  </Stack>
                </CardContent>
              </SettingCard>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Performance Tab */}
        <TabPanel value={tabValue} index={4}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <SettingCard>
                <CardHeader
                  title={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <StorageIcon color="primary" />
                      <Typography variant="h6" fontWeight={700}>
                        Data Management
                      </Typography>
                    </Stack>
                  }
                />
                <Divider />
                <CardContent>
                  <Stack spacing={2}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        background: alpha(theme.palette.warning.main, 0.1),
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                        <InfoIcon color="warning" fontSize="small" />
                        <Typography variant="body2" fontWeight={600}>
                          Cache Management
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        Clear cached data to free up space and improve performance
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      startIcon={<DeleteIcon />}
                      fullWidth
                      sx={{ py: 1.5, borderRadius: 2 }}
                      size="large"
                      color="warning"
                    >
                      Clear Cache
                    </Button>
                  </Stack>
                </CardContent>
              </SettingCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <SettingCard>
                <CardHeader
                  title={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <DownloadIcon color="primary" />
                      <Typography variant="h6" fontWeight={700}>
                        Export Data
                      </Typography>
                    </Stack>
                  }
                />
                <Divider />
                <CardContent>
                  <Stack spacing={2}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        background: alpha(theme.palette.info.main, 0.1),
                        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                        <InfoIcon color="info" fontSize="small" />
                        <Typography variant="body2" fontWeight={600}>
                          Data Export
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        Download your settings and preferences as a backup
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      fullWidth
                      sx={{ py: 1.5, borderRadius: 2 }}
                      size="large"
                      color="primary"
                    >
                      Export Settings
                    </Button>
                  </Stack>
                </CardContent>
              </SettingCard>
            </Grid>
          </Grid>
        </TabPanel>
      </GlassCard>

      {/* Logout confirm dialog */}
      <Dialog
        open={confirmLogoutOpen}
        onClose={() => setConfirmLogoutOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: { xs: "90%", sm: 400 },
          },
        }}
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <LogoutIcon color="error" />
            <Typography variant="h6" fontWeight={700}>
              Confirm Logout
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to log out? You'll be returned to the sign-in page and any unsaved work may be lost.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={() => setConfirmLogoutOpen(false)} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={doLogout} sx={{ borderRadius: 2 }}>
            Log Out
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SettingsPage;
